import { useEffect, useRef } from "react";

export const CABLE_RECONNECT_BASE_MS = 1_000;
export const CABLE_RECONNECT_MAX_MS = 30_000;

interface UseCableChannelOptions {
  apiUrl: string;
  authToken: string | null;
  /** Optional callback fired each time the WebSocket opens (after subscribe is sent). */
  onConnect?: () => void;
}

/**
 * Shared hook that owns the ActionCable WebSocket lifecycle: connect,
 * exponential backoff reconnect, dispose flag, URL construction, and
 * system-message filtering (ping/welcome/confirm_subscription).
 *
 * @param options - apiUrl, authToken, and optional onConnect callback
 * @param identifier - JSON string passed as the ActionCable channel identifier
 * @param onMessage - called with the message payload for each non-system frame
 * @returns a ref whose current value is true when the WebSocket is connected
 */
export function useCableChannel(
  { apiUrl, authToken, onConnect }: UseCableChannelOptions,
  identifier: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMessage: (message: any) => void,
): React.RefObject<boolean> {
  const connectedRef = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;

  useEffect(() => {
    if (!authToken || !apiUrl) return;

    let ws: WebSocket | null = null;
    let reconnectDelay = CABLE_RECONNECT_BASE_MS;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;

    function connect() {
      if (disposed) return;
      const wsUrl = apiUrl.replace(/^http/, "ws") + "/cable?token=" + authToken;

      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        connectedRef.current = true;
        reconnectDelay = CABLE_RECONNECT_BASE_MS;
        ws!.send(JSON.stringify({ command: "subscribe", identifier }));
        onConnectRef.current?.();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string);
          if (
            data.type === "ping" ||
            data.type === "welcome" ||
            data.type === "confirm_subscription"
          ) {
            return;
          }
          if (!data.message) return;
          onMessageRef.current(data.message);
        } catch {
          // Ignore parse errors
        }
      };

      ws.onclose = () => {
        connectedRef.current = false;
        if (!disposed) scheduleReconnect();
      };

      ws.onerror = () => ws?.close();
    }

    function scheduleReconnect() {
      if (disposed) return;
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, CABLE_RECONNECT_MAX_MS);
        connect();
      }, reconnectDelay);
    }

    connect();

    return () => {
      disposed = true;
      clearTimeout(reconnectTimer);
      ws?.close();
      connectedRef.current = false;
    };
  }, [authToken, apiUrl, identifier]);

  return connectedRef;
}
